import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useSpring, animated } from '@react-spring/web';
import useScrollFadeEffect from '../hooks/useScrollFadeEffect';

export default function AnimatedWrapper({ children, direction = 'up', style = {}, useTransform = true, delay = 0, ...props }) {
  const containerRef = useRef(null);
  const { isVisible, scrollDirection } = useScrollFadeEffect(containerRef);
  const [settled, setSettled] = useState(false);
  // capture the scroll direction at the moment the element becomes visible
  const entryDirection = useRef('down');

  // reset settled state when visibility changes to allow re-animation
  useEffect(() => {
    if (!isVisible) {
      setSettled(false);
      // prepare entrance direction for next intersection
      entryDirection.current = 'down';
    } else {
      // lock the entry direction at the moment the element first becomes visible
      entryDirection.current = scrollDirection || 'down';
    }
  }, [isVisible, scrollDirection]);

  // compute target transform once per relevant input to avoid frequent recalculations
  const targetTransform = useMemo(() => {
    if (!useTransform) return 'none';
    if (isVisible) return 'translateY(0px)';

    // when hidden, position offscreen based on the locked entry direction
    if (direction === 'left') return 'translateX(-20px)';
    if (direction === 'right') return 'translateX(20px)';
    return (entryDirection.current === 'up') ? 'translateY(-20px)' : 'translateY(20px)';
  }, [useTransform, isVisible, direction]);

  // smoother spring: slightly lower tension and tuned friction for less snappy motion
  const springConfig = useMemo(() => ({ mass: 1, tension: 220, friction: 14 }), []);

  const styles = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: targetTransform,
    delay,
    config: springConfig,
    onRest: () => {
      if (isVisible) setSettled(true);
    }
  });

  return (
    <animated.div
      ref={containerRef}
      // When the animation has finished and the element is visible, remove transform
      // to avoid keeping the element on a composited layer which can make text appear blurry
      style={{ ...styles, ...(settled && useTransform ? { transform: 'none' } : {}), ...style }}
      {...props}
    >
      {children}
    </animated.div>
  );
}